import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { MorphSelect } from '@/components/ui/MorphSelect'
import type { MorphSelectOption } from '@/components/ui/MorphSelect'
import { workspaceApi } from '@/api/workspaceApi'
import { projectApi } from '@/api/projectApi'
import { ApiError } from '@/api/httpClient'
import type { ProjectListItem, Workspace } from '@/api/types'

interface TransferProjectDialogProps {
  open: boolean
  onClose: () => void
  project: ProjectListItem | null
  currentWorkspaceId: number
  onSuccess: (targetWorkspace: Workspace) => void
}

const ERROR_CODE_TO_KEY: Record<string, string> = {
  PROJECT_TRANSFER_SAME_WORKSPACE: 'transferDialog.errors.sameWorkspace',
  PROJECT_TRANSFER_NOT_OWNER_SOURCE: 'transferDialog.errors.notOwnerSource',
  PROJECT_TRANSFER_NOT_OWNER_TARGET: 'transferDialog.errors.notOwnerTarget',
  PROJECT_TRANSFER_NAME_CONFLICT: 'transferDialog.errors.nameConflict',
  PROJECT_TRANSFER_PLAN_LIMIT: 'transferDialog.errors.planLimit',
}

export function TransferProjectDialog({
  open,
  onClose,
  project,
  currentWorkspaceId,
  onSuccess,
}: TransferProjectDialogProps) {
  const { t } = useTranslation()
  const [targetWorkspaceId, setTargetWorkspaceId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const workspaceCacheRef = useRef<Map<string, Workspace>>(new Map())

  function handleClose() {
    setTargetWorkspaceId(null)
    setError(null)
    onClose()
  }

  async function loadWorkspaceOptions(query: string): Promise<MorphSelectOption[]> {
    const params = query.trim()
      ? { name: query.trim(), my_role: 'owner' as const, limit: 10 }
      : { my_role: 'owner' as const, limit: 10 }
    const page = await workspaceApi.list(params)
    const filtered = page.results.filter((w) => w.id !== currentWorkspaceId)
    filtered.forEach((w) => workspaceCacheRef.current.set(String(w.id), w))
    return filtered.map((w) => ({ value: String(w.id), label: w.name, description: w.slug }))
  }

  async function handleConfirm() {
    if (!project || !targetWorkspaceId) return
    const targetWorkspace = workspaceCacheRef.current.get(targetWorkspaceId)
    if (!targetWorkspace) return
    setLoading(true)
    setError(null)
    try {
      await projectApi.transfer(project.id, targetWorkspace.id)
      handleClose()
      onSuccess(targetWorkspace)
    } catch (err) {
      if (err instanceof ApiError) {
        const key = ERROR_CODE_TO_KEY[err.code]
        setError(key ? t(key) : err.message)
      } else {
        setError(t('transferDialog.unexpectedError'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      title={t('transferDialog.title')}
      onClose={handleClose}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={loading}>
            {t('transferDialog.cancel')}
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            disabled={!targetWorkspaceId || loading}
          >
            {loading ? t('transferDialog.moving') : t('transferDialog.move')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4 }}>{t('transferDialog.projectLabel')}</div>
          <div style={{ fontWeight: 500 }}>{project?.name ?? '—'}</div>
        </div>

        <div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>{t('transferDialog.targetWorkspaceLabel')}</div>
          <MorphSelect
            value={targetWorkspaceId}
            onChange={(val) => setTargetWorkspaceId(val)}
            loadOptions={loadWorkspaceOptions}
            searchable
            clearable
            placeholder={t('transferDialog.selectPlaceholder')}
            searchPlaceholder={t('transferDialog.searchPlaceholder')}
            emptyMessage={t('transferDialog.emptyMessage')}
            disabled={loading}
          />
        </div>

        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              background: 'var(--color-danger-bg)',
              border: '1px solid var(--color-danger-border)',
              color: 'var(--color-danger)',
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}
      </div>
    </Dialog>
  )
}
